import React, { useState } from 'react';
import { useSubscription } from '../context/SubscriptionContext';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Lock, Loader2, LogOut, Crown, CheckCircle2 } from 'lucide-react';
import { subscriptionApi } from '../services/api';
import { toast } from './ui/sonner';

/**
 * TrialExpiredPaywall
 * --------------------------------------------------------------
 * Non-dismissible full-screen modal shown whenever the user's
 * subscription.status === 'expired' OR 'canceled'.
 *
 * Mounted globally inside the protected app shell. When triggered,
 * it covers the entire viewport so the user CANNOT keep using the
 * app for free — they must either upgrade or sign out.
 *
 * Read-only access to existing data continues to work via the
 * backend (GETs are not gated), but every write now returns 402,
 * which keeps the app safe even if the paywall is bypassed via
 * devtools.
 */
const TrialExpiredPaywall = () => {
  const { isExpired, subscription } = useSubscription();
  const { logout, user } = useAuth();
  const [loading, setLoading] = useState(null);

  if (!isExpired) return null;

  const isCanceled = subscription?.status === 'canceled';

  const startCheckout = async (plan) => {
    setLoading(plan);
    try {
      const { data } = await subscriptionApi.checkout(plan);
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      toast.error('Could not start checkout. Please try again.');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Could not start checkout.';
      toast.error(typeof detail === 'string' ? detail : 'Could not start checkout.');
    } finally {
      setLoading(null);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      // logout always clears local state, but the request can still fail
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Server-side logout failed; client state cleared anyway:', err);
      }
    }
    window.location.href = '/';
  };

  return (
    <div
      className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      data-testid="trial-expired-paywall"
      role="dialog"
      aria-modal="true"
      aria-labelledby="paywall-heading"
    >
      <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full p-8 sm:p-10 my-auto">
        <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-6">
          <Lock className="w-8 h-8 text-amber-600" />
        </div>

        <h2
          id="paywall-heading"
          className="font-heading text-2xl sm:text-3xl font-bold text-ep-text text-center mb-3"
        >
          {isCanceled ? 'Your subscription has been canceled.' : 'Your free trial has ended.'}
        </h2>
        <p className="text-ep-text-secondary text-center text-base mb-2">
          {user?.first_name ? `Hi ${user.first_name}, ` : ''}
          {isCanceled
            ? 'Reactivate to keep editing your estates and generating reports.'
            : 'Pick a plan to continue managing your estates.'}
        </p>
        <p className="text-xs text-ep-text-muted text-center mb-8">
          Your data is safe — read-only access remains available until you upgrade.
        </p>

        <div className="grid sm:grid-cols-2 gap-4 mb-6">
          {/* Per-Estate */}
          <button
            type="button"
            onClick={() => startCheckout('per_estate')}
            disabled={loading !== null}
            data-testid="paywall-per-estate-btn"
            className="text-left p-5 rounded-2xl border-2 border-ep-border hover:border-ep-primary hover:shadow-lifted transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-ep-secondary">Per Estate</span>
              <span className="text-xl font-bold text-ep-text">$149.99</span>
            </div>
            <p className="text-sm text-ep-text-secondary mb-3">One-time charge. One estate, all features.</p>
            <ul className="text-xs space-y-1 text-ep-text-secondary mb-4">
              <li className="flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-emerald-600" />Unlimited reports</li>
              <li className="flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-emerald-600" />Court-ready PDFs</li>
              <li className="flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-emerald-600" />Full collaboration</li>
            </ul>
            <div className="inline-flex items-center text-ep-primary font-medium text-sm">
              {loading === 'per_estate' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Buy now →'}
            </div>
          </button>

          {/* Professional */}
          <button
            type="button"
            onClick={() => startCheckout('professional')}
            disabled={loading !== null}
            data-testid="paywall-professional-btn"
            className="text-left p-5 rounded-2xl border-2 border-ep-primary bg-ep-primary/5 hover:shadow-lifted transition-all disabled:opacity-60 disabled:cursor-not-allowed relative"
          >
            <div className="absolute -top-2 right-3 bg-ep-primary text-white text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full flex items-center gap-1">
              <Crown className="w-2.5 h-2.5" /> Best Value
            </div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-ep-primary">Professional</span>
              <div className="text-right">
                <span className="text-xl font-bold text-ep-text">$49.99</span>
                <span className="text-xs text-ep-text-muted">/mo</span>
              </div>
            </div>
            <p className="text-sm text-ep-text-secondary mb-3">Unlimited estates for attorneys & pros.</p>
            <ul className="text-xs space-y-1 text-ep-text-secondary mb-4">
              <li className="flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-emerald-600" />Unlimited estates</li>
              <li className="flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-emerald-600" />Unlimited reports</li>
              <li className="flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-emerald-600" />Priority support</li>
            </ul>
            <div className="inline-flex items-center text-ep-primary font-medium text-sm">
              {loading === 'professional' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Subscribe →'}
            </div>
          </button>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-ep-border/40">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="text-ep-text-muted hover:text-ep-text"
            data-testid="paywall-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-1.5" />
            Sign out
          </Button>
          <a
            href="/pricing"
            className="text-sm text-ep-text-secondary hover:text-ep-primary"
            data-testid="paywall-compare-link"
          >
            Compare all plans →
          </a>
        </div>
      </div>
    </div>
  );
};

export default TrialExpiredPaywall;
